import { useEffect, useState } from "react";
import api from "../lib/api";
import { Trophy, Bike } from "lucide-react";
import RaceCard from "../components/RaceCard";

export default function RankingPage() {
  const [years, setYears] = useState([]);
  const [year, setYear] = useState(null); // number | "all"
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [yearsLoaded, setYearsLoaded] = useState(false);

  // Fetch available years from rides
  useEffect(() => {
    api
      .get("/rides/years")
      .then((res) => {
        const yrs = res.data?.years || [];
        setYears(yrs);
        if (yrs.length > 0) {
          const currentYear = new Date().getFullYear();
          const preferred = yrs.includes(currentYear) ? currentYear : yrs[0];
          setYear(preferred);
        } else {
          setYear(2026);
        }
      })
      .finally(() => setYearsLoaded(true));
  }, []);

  useEffect(() => {
    if (year === null || year === undefined) return;
    setLoading(true);
    api
      .get("/ranking", { params: { year } })
      .then((res) => setData(res.data))
      .finally(() => setLoading(false));
  }, [year]);

  const displayYears = years.length > 0 ? years : [2026];

  return (
    <div className="max-w-5xl mx-auto px-4 sm:px-6 py-8 sm:py-12">
      <div className="flex items-end justify-between flex-wrap gap-4 mb-8">
        <div>
          <div className="label-caps" style={{ color: "#00E5FF" }}>
            Speed Ranking
          </div>
          <h1 className="font-display text-4xl sm:text-5xl text-white uppercase flex items-center gap-3">
            <Trophy size={36} style={{ color: "#39FF14" }} />
            Motociclistas do Ano
          </h1>
          <p className="text-white/60 text-sm mt-2">
            Quem marcou presença nos melhores rolês?
          </p>
        </div>

        <div className="flex gap-1 border border-white/10 bg-[#12141D] p-1 rounded-sm flex-wrap" data-testid="ranking-year-tabs">
          {displayYears.map((y) => (
            <button
              key={y}
              onClick={() => setYear(y)}
              className={`px-4 py-2 rounded-sm transition-colors font-bold uppercase tracking-widest text-xs ${
                y === year
                  ? "bg-[#00E5FF] text-black"
                  : "text-white/70 hover:text-white"
              }`}
              data-testid={`ranking-year-${y}`}
            >
              {y}
            </button>
          ))}
          <button
            onClick={() => setYear("all")}
            className={`px-4 py-2 rounded-sm transition-colors font-bold uppercase tracking-widest text-xs ${
              year === "all"
                ? "bg-[#39FF14] text-black"
                : "text-white/70 hover:text-white"
            }`}
            data-testid="ranking-year-all"
            title="Ranking vitalício"
          >
            Todos os anos
          </button>
        </div>
      </div>

      {loading ? (
        <div className="label-caps">Carregando...</div>
      ) : data && data.riders.length > 0 ? (
        <>
          <div className="mb-8 flex items-center gap-4 text-white/70 text-sm">
            <div>
              <span className="label-caps text-white/50 block">
                {year === "all" ? "Rolês (vitalício)" : "Rolês no ano"}
              </span>
              <span className="font-display text-3xl text-white">
                {data.total_rides}
              </span>
            </div>
            <div className="w-px h-10 bg-white/10" />
            <div>
              <span className="label-caps text-white/50 block">Motociclistas</span>
              <span className="font-display text-3xl text-white">
                {data.riders.length}
              </span>
            </div>
          </div>

          {/* Podium — Race Cards top 3, 1st centered/larger */}
          <PodiumTop3 riders={data.riders.slice(0, 3)} />

          {/* Rest of the ranking (4+) as a compact list */}
          {data.riders.length > 3 && (
            <div className="border border-white/10 bg-[#12141D]" data-testid="ranking-list">
              {data.riders.slice(3).map((r, i) => {
                const idx = i + 3;
                return (
                  <div
                    key={`${r.name}-${idx}`}
                    className="flex items-center gap-4 px-4 py-3 border-b border-white/5 last:border-b-0 hover:bg-white/5 transition-colors"
                    data-testid={`ranking-row-${idx}`}
                  >
                    <div className="font-display text-3xl w-10 text-center text-white/60">
                      {String(idx + 1).padStart(2, "0")}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="text-white font-semibold truncate flex items-center gap-2">
                        {r.name}
                        {r.is_guest && (
                          <span
                            className="label-caps text-[10px] px-1.5 py-0.5 border"
                            style={{ color: "#F59E0B", borderColor: "rgba(245,158,11,0.35)" }}
                          >
                            Convidado
                          </span>
                        )}
                      </div>
                      {r.motorcycles.length > 0 && (
                        <div className="flex items-center gap-1 text-white/50 text-xs mt-1 truncate">
                          <Bike size={12} />
                          <span className="truncate">{r.motorcycles.join(" · ")}</span>
                        </div>
                      )}
                    </div>
                    <div className="text-right shrink-0">
                      <div className="font-display text-2xl text-[#00E5FF] glow-blue">
                        {r.rides_count}
                      </div>
                      <div className="label-caps text-[10px]">
                        {r.rides_count === 1 ? "rolê" : "rolês"}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </>
      ) : (
        <div
          className="border border-dashed border-white/10 bg-[#12141D] py-16 text-center"
          data-testid="ranking-empty"
        >
          <div className="label-caps text-white/60">
            {year === "all" ? "Sem rolês registrados ainda" : `Sem rolês em ${year} ainda`}
          </div>
          <p className="text-white text-lg mt-2 font-display uppercase">
            Bora fazer história
          </p>
        </div>
      )}
    </div>
  );
}

function PodiumTop3({ riders }) {
  if (!riders || riders.length === 0) return null;
  const first = riders[0];
  const second = riders[1];
  const third = riders[2];

  return (
    <div className="mb-10" data-testid="ranking-podium">
      {/* Desktop layout: 2 | 1 | 3   — 1 centered, larger, stronger neon.
          The center column is wider (1.4fr) so #1's card grows physically bigger. */}
      <div
        className="hidden sm:grid gap-4 sm:gap-6 items-end"
        style={{ gridTemplateColumns: "1fr 1.4fr 1fr" }}
      >
        <div className="pb-8">
          {second ? (
            <RaceCard
              rider={second}
              variant="podium"
              position={2}
              accent="#C0C0C0"
              testId="podium-2"
            />
          ) : (
            <div />
          )}
        </div>
        <div className="-mt-4 sm:-mt-10">
          <RaceCard
            rider={first}
            variant="podium"
            position={1}
            accent="#00E5FF"
            testId="podium-1"
          />
        </div>
        <div className="pb-8">
          {third ? (
            <RaceCard
              rider={third}
              variant="podium"
              position={3}
              accent="#39FF14"
              testId="podium-3"
            />
          ) : (
            <div />
          )}
        </div>
      </div>

      {/* Mobile: 1st on top, then 2 + 3 side by side */}
      <div className="sm:hidden space-y-4">
        <RaceCard
          rider={first}
          variant="podium"
          position={1}
          accent="#00E5FF"
          testId="podium-1"
        />
        <div className="grid grid-cols-2 gap-3">
          {second ? (
            <RaceCard
              rider={second}
              variant="podium"
              position={2}
              accent="#C0C0C0"
              testId="podium-2"
            />
          ) : null}
          {third ? (
            <RaceCard
              rider={third}
              variant="podium"
              position={3}
              accent="#39FF14"
              testId="podium-3"
            />
          ) : null}
        </div>
      </div>
    </div>
  );
}
